import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { Day } from "./Day";
import MealPlanFormStore from "./MealPlanFormStore";
import { PlannedMeal } from "./PlannedMeal";
import { PlannedMealItem } from "./PlannedMealItem";

export class Cell {
  dayColumn: Day;
  plannedMeal: PlannedMeal;
  items: PlannedMealItem[] = [];

  isDeleting = false;

  private store: MealPlanFormStore;

  get key(): string {
    return [this.dayColumn.index, this.plannedMeal.uuid].join("_");
  }

  get label(): string {
    return [this.plannedMeal.label, this.dayColumn.label].join(" ");
  }

  get totalEnergyKcal(): number {
    return this.items.reduce((total, item) => total + item.energyKcal, 0);
  }

  get isActive(): boolean {
    return this.store.cells.active === this;
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  constructor(
    store: MealPlanFormStore,
    plannedMealRow: PlannedMeal,
    dayColumn: Day
  ) {
    makeAutoObservable(this);
    this.store = store;
    this.plannedMeal = plannedMealRow;
    this.dayColumn = dayColumn;
  }

  addItem(
    food: PlannedMealItem["food"],
    foodPortion: PlannedMealItem["foodPortion"],
    amount: number,
    uuid = uuidv4()
  ): void {
    const existing = this.items.find((i) => i.food.id === food.id);
    // If food item is already added, just update the selected food portion and
    // amount.
    if (existing) {
      existing.foodPortion = foodPortion;
      existing.amount = amount;
    } else {
      this.items.push(
        new PlannedMealItem(this.store, this, food, foodPortion, amount, uuid)
      );
    }
  }

  removeItem(item: PlannedMealItem): void {
    const index = this.items.indexOf(item);
    if (index !== -1) this.items.splice(index, 1);
    if (this.isEmpty) this.isDeleting = false;
  }

  replaceItems(newItems: PlannedMealItem[]): void {
    this.items = newItems.map(
      (i) =>
        new PlannedMealItem(this.store, this, i.food, i.foodPortion, i.amount)
    );
  }

  clearItems(): void {
    this.items = [];
  }

  toggleDeleting(): void {
    this.isDeleting = !this.isDeleting;
  }
}
